import { useEffect, useState } from 'react'
import { List, fromJS, Map } from 'immutable'
import _get from 'lodash/get'
import _isArray from 'lodash/isArray'
import {
  formatBillingPlans,
  BILLING_PLAN_GRID_LOCATION_CHECKOUT,
} from 'services/plans/utils'
import { useLazyQuery } from '@apollo/client'
import { GET_PLANS, GET_PLANS_BY_SKU } from 'services/plans/query'
import { get as getConfig } from 'config'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from 'services/apollo-graphql'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

// Utility function to detect Apollo Client store reset errors
const isApolloStoreResetError = (error) => {
  if (!error) return false

  // Check main error message
  if (error.message) {
    const message = error.message.toLowerCase()
    if (message.includes('store reset while query was in flight')
        || message.includes('not completed in link chain')
        || message.includes('invariant violation')
        || message.includes('apollo client has been stopped')
        || message.includes('query was cancelled')
        || message.includes('network request failed')) {
      return true
    }
  }

  // Check GraphQL errors
  if (error.graphQLErrors && Array.isArray(error.graphQLErrors)) {
    return error.graphQLErrors.some((err) => err.message && (
      err.message.toLowerCase().includes('store reset while query was in flight')
      || err.message.toLowerCase().includes('not completed in link chain')
    ))
  }

  // Check network errors
  if (error.networkError) {
    const { networkError } = error
    if (networkError.message) {
      const message = networkError.message.toLowerCase()
      if (message.includes('store reset while query was in flight')
          || message.includes('not completed in link chain')
          || message.includes('network request failed')) {
        return true
      }
    }
  }

  // Check for Apollo Client's internal error metadata
  if (error.extraInfo && error.extraInfo.storeReset) {
    return true
  }

  return false
}

/**
 * React hook for fetching plans for the plan grid (checkout, changePlan, gift)
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {String} option.page - The location/page for the plan grid (checkout, etc)
 * @param {Boolean} option.forceRefresh - Whether to force refresh the plans
 * @returns {List} - Formatted List of plans
 */
export function useGetPlanGridData (options) {
  const {
    userLanguage,
    authJwt,
    page = BILLING_PLAN_GRID_LOCATION_CHECKOUT,
    forceRefresh,
  } = options
  const [getPlansData, { data: gqlPlanData, error }] = useLazyQuery(GET_PLANS, {
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
  })
  const [planGridUserLanguage, setPlanGridUserLanguage] = useState(userLanguage)
  const [planGridAuthJwt, setPlanGridAuthJwt] = useState(authJwt)
  const [retryCount, setRetryCount] = useState(0)
  const userLanguageChanged = userLanguage !== planGridUserLanguage
  const authJwtChanged = authJwt !== planGridAuthJwt

  // Format the plans data
  const formattedPlans = fromJS(formatBillingPlans(gqlPlanData)) || List()

  useEffect(() => {
    if (planGraphQLActive) {
      getPlansData({
        variables: {
          language: userLanguage,
          planGrid: true,
          page,
        },
        fetchPolicy: authJwtChanged || forceRefresh
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userLanguageChanged, authJwtChanged, forceRefresh, getPlansData, userLanguage, page, authJwt])

  // Handle store reset errors and retry the query
  useEffect(() => {
    if (isApolloStoreResetError(error) && retryCount < 3) {
      // Wait a bit for the store to be fully reset, then retry
      // Use exponential backoff for retries
      const delay = Math.min(100 * (2 ** retryCount), 1000)
      const timeoutId = setTimeout(() => {
        setRetryCount((prev) => prev + 1)
        getPlansData({
          variables: {
            language: userLanguage,
            planGrid: true,
            page,
          },
          fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
          errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
          context: {
            jwt: authJwt,
          },
        })
      }, delay)

      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [error, retryCount, userLanguage, page, authJwt, getPlansData])

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setPlanGridUserLanguage(userLanguage)
  }, [userLanguage])

  // set the user's jwt for this page to help handle loading plans
  // if the user's jwt changes, then load plans again
  useEffect(() => {
    setPlanGridAuthJwt(authJwt)
    // Reset retry count when JWT changes
    setRetryCount(0)
  }, [authJwtChanged])

  return formattedPlans
}

/**
 * React hook for fetching plans by sku
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {String|String[]} option.planSkus - A single sku (string) or an Array of sku strings
 * @param {Boolean} option.forceRefresh - Whether to force refresh the plans
 * @returns {List} - Formatted List of plans
 */
export function useGetPlanDataBySku (options) {
  const {
    planSkus,
    userLanguage,
    authJwt,
    forceRefresh,
  } = options
  const [
    getPlansDataBySku,
    { data: planData, loading, error },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const formattedPlans = fromJS(formatBillingPlans(planData)) || List()
  const [planGridUserLanguage, setPlanGridUserLanguage] = useState(userLanguage)
  const [planGridAuthJwt, setPlanGridAuthJwt] = useState(authJwt)
  const userLanguageChanged = userLanguage !== planGridUserLanguage
  const authJwtChanged = authJwt !== planGridAuthJwt
  const skus = _isArray(planSkus) ? planSkus : [planSkus]

  useEffect(() => {
    if (planGraphQLActive && planSkus) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus,
        },
        fetchPolicy: authJwtChanged || forceRefresh
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [planSkus, userLanguageChanged, authJwtChanged, forceRefresh])

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setPlanGridUserLanguage(userLanguage)
  }, [userLanguage])

  // set the user's jwt for this page to help handle loading plans
  // if the user's jwt changes, then load plans again
  useEffect(() => {
    setPlanGridAuthJwt(authJwt)
  }, [authJwtChanged])

  return Map({
    plans: formattedPlans,
    loading,
    error,
    complete: !loading && !!planData,
  })
}

/**
 * React hook for fetching plans for the plan grid (checkout, changePlan, gift)
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {Map} option.selectedPlan - The user's selectedPlan
 * @param {String} option.userAccountCurrencyIso - The user's account currency iso
 * @param {Function} options.setPlansSelection - Set plan selection action
 */
export function useUpdateSelectedPlan (options) {
  const {
    selectedPlan = Map(),
    setPlansSelection,
    userLanguage,
    authJwt,
    userAccountCurrencyIso,
  } = options
  const [
    getPlansDataBySku,
    { data: planData },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const [selectedPlanLanguage, setSelectedPlanLanguage] = useState(userLanguage)
  const selectedPlanSku = selectedPlan.get('sku')
  const userLanguageChanged = userLanguage !== selectedPlanLanguage
  const formattedPlans = fromJS(formatBillingPlans(planData)) || List()
  const incomingPlan = userLanguageChanged ? Map() : formattedPlans.find((p) => p.get('sku') === selectedPlanSku) || Map()

  useEffect(() => {
    if (planGraphQLActive && selectedPlanSku) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus: selectedPlanSku,
        },
        fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
        errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userLanguageChanged, userAccountCurrencyIso])

  // set the user language for this the selected to help handle loading plans
  // if the user changes their language
  useEffect(() => {
    setSelectedPlanLanguage(userLanguage)
  }, [userLanguage])

  // set the user's selected plan in Redux if the language changed
  if (Map.isMap(incomingPlan) && incomingPlan.size && incomingPlan.get('billingSchedule') !== selectedPlan.get('billingSchedule')) {
    setPlansSelection(incomingPlan)
  }

  // set the user's selected plan if the currency changed
  if (Map.isMap(incomingPlan) && incomingPlan.size && incomingPlan.get('currencyIso') !== selectedPlan.get('currencyIso')) {
    setPlansSelection(incomingPlan)
  }
}
